import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import classnames from '../../../../core/utils/classnames.tsx';
import { useEscapeKey } from '../../../../core/utils/hooks/useEscapeKey.ts';

import { Bbox } from '../types.ts';

export interface IArea /* extends React.SVGAttributes<SVGRectElement> */ {
    bbox?: Bbox;
    pageIndex?: number;
    numPages?: number;
    disabled?: boolean;
    data?: any;
    className?: string;
    visible?: boolean;

    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    borderRadius?: number;
    padding?: number;

    onClick?: any;
    onMouseMove?: any;
    onMouseDown?: any;
    onMouseUp?: any;
    onMouseEnter?: any;
    onMouseLeave?: any;
}

export const getBboxForImageMap = (bbox, pageIndex = 0, numPages = 1) => {
    if (bbox === undefined || bbox === null || !bbox?.length) return;

    let [[x1, y1], [x2, y2]] = bbox;

    y1 = (y1 + pageIndex) / numPages;
    y2 = (y2 + pageIndex) / numPages;

    return [
        [x1, y1],
        [x2, y2],
    ];
};

/*
I was thinking about using React.memo here with a custom shouldUpdate method based
on what properties would change how the area is drawn.
Sadly state is used in e.g. onClick as well so if state changes those words
need to be redrawn. Therefore I decided to keep it simple.

Anyway - for hover and colors consider using CSS as we don't
need to redraw all areas then.
 */

export const Area = React.memo((props: IArea) => {
    const {
        bbox = [
            [0, 0],
            [0, 0],
        ],
        pageIndex = 0,
        numPages = 1,
        disabled = false,
        borderRadius = 1.5, // TODO: if this should become a library remove this one
        padding = 0.001, // TODO: if this should become a library remove this one
        data,
        className,
        onClick,
        onMouseMove,
        onMouseDown,
        onMouseUp,
        onMouseEnter,
        onMouseLeave,
        ...svgProps
    } = props;
    const scaledBbox = getBboxForImageMap(bbox, pageIndex, numPages);
    if (!scaledBbox) return;

    const [left, top, right, bottom] = scaledBbox.flat();
    const formatPos = (val) => `${val * 100}%`;

    const scaledPadding = padding / numPages;
    const real_left = Math.min(left, right);
    const real_top = Math.min(top, bottom);
    const real_right = Math.max(left, right);
    const real_bottom = Math.max(top, bottom);
    const positionProps = {
        x: formatPos(real_left - 2 * padding),
        y: formatPos(real_top - scaledPadding),
        width: formatPos(real_right - real_left + 4 * padding),
        height: formatPos(real_bottom - real_top + 2 * scaledPadding),
    };

    return (
        <rect
            {...positionProps}
            {...svgProps}
            onClick={(e) => onClick?.(e, data)}
            onMouseMove={(e) => onMouseMove?.(e, data)}
            onMouseDown={(e) => onMouseDown?.(e, data)}
            onMouseUp={(e) => onMouseUp?.(e, data)}
            onMouseEnter={(e) => onMouseEnter?.(e, data)}
            onMouseLeave={(e) => onMouseLeave?.(e, data)}
            rx={borderRadius}
            ry={borderRadius}
            className={classnames('mix-blend-multiply stroke-transparent transition-colors', className)}
            style={disabled ? { pointerEvents: 'none' } : null}
        />
    );
});

export interface IProps {
    children?: any;
    numPages?: number;
    className?: string;

    src: string;
    fillColor?: string;
    strokeColor?: string;
    lineWidth?: number;
    height?: string;

    onLoad?: any;
    onClick?: any;
    onPointerMove?: any;
    onPointerDown?: any;
    onPointerUp?: any;
    onMouseEnter?: any;
    onMouseLeave?: any;
}

export const ImageMap = (props: IProps) => {
    const { children, className, src, onLoad, numPages = 1, ...svgProps } = props;

    const [dimensions, setDimensions] = useState([0, 0]);
    const [imageWidth, imageHeight] = dimensions;

    // auto scale to source image dimensions
    useEffect(() => {
        const newImg = new Image();
        newImg.onload = (e) => {
            const imageHeight = newImg.height;
            const imageWidth = newImg.width;
            setDimensions([imageWidth, imageHeight]);
            onLoad?.(e);
        };
        newImg.src = src; // this must be done AFTER setting onload
    }, [src]);

    return (
        <svg
            width="100%"
            height="100%"
            {...svgProps}
            viewBox={`0 0 ${imageWidth} ${imageHeight}`}
            className={className}
        >
            <image className="pointer-events-none w-full h-full" href={src} />
            {React.Children.map(
                children,
                (child, i) => child && <child.type key={child.key} {...child.props} numPages={numPages} />
            )}
        </svg>
    );
};

export default ImageMap;

export const getMousePositionRelative = (evt: React.MouseEvent<SVGSVGElement>) => {
    const svg = evt.currentTarget;
    const { width, height } = svg.viewBox.baseVal;
    const ctm = svg.getScreenCTM();
    if (!ctm || !width || !height) return { x: 0, y: 0 };

    // the element box and the viewBox only agree by accident (borders, aspect rounding), so map through the svg transform
    const point = new DOMPoint(evt.clientX, evt.clientY).matrixTransform(ctm.inverse());
    return {
        x: Math.min(Math.max(point.x / width, 0), 1),
        y: Math.min(Math.max(point.y / height, 0), 1),
    };
};

export const sortBbox = (bbox) => {
    const [x1, y1] = bbox[0];
    const [x2, y2] = bbox[1];

    const top = x1 < x2 ? x1 : x2;
    const bottom = x1 > x2 ? x1 : x2;
    const left = y1 < y2 ? y1 : y2;
    const right = y1 > y2 ? y1 : y2;

    return [
        [top, left],
        [bottom, right],
    ];
};

export const getBboxFromImageMap = (bbox, pageIndex = 0, numPages = 1, acrossPages = false) => {
    if (bbox === undefined) return bbox;

    const [[x1, y1], [x2, y2]] = bbox;
    // a drag may leave the page it started on; across pages a y below 0 or above 1 lies on the previous or next pages,
    // otherwise the selection is clamped to that page instead of wrapping around
    const toPage = (y) => {
        const pageY = y * numPages - pageIndex;
        return acrossPages ? pageY : Math.min(Math.max(pageY, 0), 1);
    };

    return [
        [x1, toPage(y1)],
        [x2, toPage(y2)],
    ];
};

export const getPageIndexForPosition = (y, numPages = 1) => Math.min(Math.floor(y * numPages), numPages - 1);

export const SelectionImageMap = React.memo(
    ({
        canSelect = false,
        canSelectAcrossPages = false,
        className,
        onSelectionStart,
        onSelection,
        onSelectionEnd,
        onSelectionCancel,
        children,
        numPages,
        ...mapProps
    }: any) => {
        const [isDrag, setIsDrag] = useState(false);
        const [selectedArea, setSelectedArea] = useState([0, 0, 0, 0]); // [startX, startY, endX, endY]
        const selectionFrameRef = useRef(0);
        const getSelectedBbox = React.useCallback(
            (flatBbox) =>
                sortBbox([
                    [flatBbox[0], flatBbox[1]],
                    [flatBbox[2], flatBbox[3]],
                ]) as Bbox,
            []
        );
        // the selection belongs to the page the drag started on
        const getPageIndex = React.useCallback(
            (flatBbox) => getPageIndexForPosition(flatBbox[1], numPages),
            [numPages]
        );
        const getScaledBbox = React.useCallback(
            (flatBbox) =>
                getBboxFromImageMap(getSelectedBbox(flatBbox), getPageIndex(flatBbox), numPages, canSelectAcrossPages),
            [numPages, canSelectAcrossPages, getSelectedBbox, getPageIndex]
        );

        // Memoize these calculations to prevent unnecessary recalculations
        const pageIndex = React.useMemo(() => getPageIndex(selectedArea), [selectedArea, getPageIndex]);
        const scaledSelectedBbox = React.useMemo(() => getScaledBbox(selectedArea), [selectedArea, getScaledBbox]);

        // reset selected area everytime canSelect is turned on or off
        useEffect(() => {
            setIsDrag(false);
            setSelectedArea([0, 0, 0, 0]);
        }, [canSelect]);

        useEscapeKey(isDrag, () => {
            cancelAnimationFrame(selectionFrameRef.current);
            setIsDrag(false);
            setSelectedArea([0, 0, 0, 0]);
            if (onSelectionCancel) onSelectionCancel();
        });

        const onPointerDown = React.useCallback(
            (e: React.PointerEvent<SVGSVGElement>) => {
                // touch keeps scrolling the document instead of selecting
                if (!canSelect || e.pointerType === 'touch') return;
                setIsDrag(true);
                const pos = getMousePositionRelative(e);
                const bbox = [pos.x, pos.y, pos.x, pos.y];
                setSelectedArea(bbox);
                if (onSelectionStart) onSelectionStart(getScaledBbox(bbox), getPageIndex(bbox));
            },
            [canSelect, onSelectionStart, getScaledBbox, getPageIndex]
        );

        const onPointerMove = React.useCallback(
            (e: React.PointerEvent<SVGSVGElement>) => {
                if (!isDrag || !canSelect) return;
                // keeps the drag alive over the canvas controls and outside the viewport; captured on move, not on
                // down, so that a plain click still reaches the areas
                if (!e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.setPointerCapture(e.pointerId);
                const pos = getMousePositionRelative(e);
                const [x1, y1] = selectedArea;
                const bbox = [x1, y1, pos.x, pos.y];

                // Use requestAnimationFrame to limit updates to browser's refresh rate
                cancelAnimationFrame(selectionFrameRef.current);
                selectionFrameRef.current = requestAnimationFrame(() => {
                    setSelectedArea(bbox);
                    if (onSelection) onSelection(getScaledBbox(bbox), getPageIndex(bbox));
                });
            },
            [isDrag, canSelect, selectedArea, onSelection, getScaledBbox, getPageIndex]
        );

        // no drag after Escape aborted it (or for touch, which never starts one), so the release is ignored
        const onPointerUp = React.useCallback(() => {
            if (!isDrag || !canSelect) return;
            setIsDrag(false);
            if (onSelectionEnd) onSelectionEnd(scaledSelectedBbox, pageIndex);
            setSelectedArea([0, 0, 0, 0]); // reset
        }, [isDrag, canSelect, onSelectionEnd, scaledSelectedBbox, pageIndex]);

        // TODO: maybe there is smarter way
        // after initial mousedown x1 == x2 and y1 == y2
        const isDisabled = !(selectedArea[0] == selectedArea[2] && selectedArea[1] == selectedArea[3]);

        // Only re-render the selection area when it actually changes
        const selectionArea = React.useMemo(
            () => (
                <Area
                    key="selection"
                    bbox={scaledSelectedBbox}
                    pageIndex={pageIndex}
                    className="stroke-1 cursor-crosshair pointer-events-none stroke-surface-success-strong fill-[#c5ffed] opacity-80 [contain:strict]"
                    padding={0}
                />
            ),
            [scaledSelectedBbox, pageIndex]
        );

        return (
            <ImageMap
                {...mapProps}
                numPages={numPages}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                className={classnames(className, canSelect && 'cursor-crosshair')}
            >
                {React.Children.map(
                    children,
                    (child, i) => child && <child.type key={child.key} {...child.props} disabled={isDisabled} />
                )}

                {selectionArea}
            </ImageMap>
        );
    }
);
